import React from "react";
import { format } from "date-fns";
import Pagination from "../Common/Pagination";
import Avatar from "../Common/Avatar";
import { useTranslation } from "../../contexts/LanguageContext";

export default function TicketsTable({
  tickets = [],
  currentPage = 1,
  totalPages = 1,
  total = 0,
  itemsPerPage = 10,
  onPageChange,
  onTicketClick
}) {
  const { t, tStatus } = useTranslation();
  const startIndex = (currentPage - 1) * itemsPerPage;

  const getStatusBadgeColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open':
        return 'bg-green-100 text-green-800';
      case 'in_progress':
        return 'bg-yellow-100 text-yellow-800';
      case 'closed':
        return 'bg-black text-white';
      case 'resolved':
        return 'bg-orange-500 text-white';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCategoryBadgeColor = (category) => {
    switch (category?.toLowerCase()) {
      case 'general':
        return 'bg-purple-100 text-purple-800';
      case 'support':
        return 'bg-cyan-100 text-cyan-800';
      case 'technical':
        return 'bg-red-100 text-red-800';
      case 'billing':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMM dd, yyyy HH:mm");
    } catch (error) {
      return "N/A";
    }
  };

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-dashboardStroke">
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.srNo")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("tickets.col.ticketNo")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.user")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.email")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("tickets.col.subject")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("tickets.col.category")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.status")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("tickets.col.replies")}</th>
              <th className="px-4 py-4 text-left text-[17px] font-semibold text-dashboardText">{t("col.createdAt")}</th>
            </tr>
          </thead>
          <tbody>
            {tickets.length === 0 ? (
              <tr>
                <td colSpan="9" className="px-4 py-8 text-center text-dashboardText">
                  {t("tickets.noFound")}
                </td>
              </tr>
            ) : (
              tickets.map((ticket, index) => (
                <tr 
                  key={ticket._id}
                  onClick={() => onTicketClick && onTicketClick(ticket)}
                  className={`border-b border-dashboardStroke hover:bg-gray-50 transition-colors cursor-pointer ${
                    index === 0 ? "shadow-[inset_3px_0px_0px_#FF7C17]" : ""
                  }`}
                >
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {startIndex + index + 1}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-medium text-dashboardCoral">
                    #{ticket.ticketNo || "N/A"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      {ticket.userId?.profilePicture ? (
                        <img 
                          src={ticket.userId.profilePicture} 
                          alt={ticket.userId.name}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                      ) : (
                        <Avatar name={ticket.userId?.name || "User"} size="sm" />
                      )}
                      <span className="text-base font-normal text-dashboardDarkText">
                        {ticket.userId?.name || "N/A"}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {ticket.userId?.email || "N/A"}
                  </td>
                  <td className="px-4 py-4 text-base font-normal text-dashboardDarkText max-w-xs truncate">
                    {ticket.subject || "N/A"}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getCategoryBadgeColor(ticket.category)}`}>
                      {ticket.category || "N/A"}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getStatusBadgeColor(ticket.ticketStatus)}`}>
                      {tStatus(ticket.ticketStatus)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {ticket.replies?.length || 0}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-base font-normal text-dashboardDarkText">
                    {formatDate(ticket.createdAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="px-4 py-4 border-t border-dashboardStroke">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </>
  );
}
